import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@repo/auth";
import type { AuthClaims, Identity } from "@repo/auth";
import { fetchVenues } from "./staffApi";
import type { VenueSummary } from "./staffApi";

export interface StaffContext {
  user: Identity | null;
  claims: AuthClaims;
  venues: VenueSummary[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * The staff bootstrap: identity + claims from the token (@repo/auth) fused with the assigned-venue
 * list from the single `["me"]` query. Every shell surface that needs the venue list (switcher,
 * picker, `/v/:venueId` resolution) reads THIS — react-query dedupes the fetch to one round-trip.
 */
export function useStaffContext(): StaffContext {
  const { user, claims, isAuthenticated, isLoading: authIsLoading } = useAuth();
  const query = useQuery({
    queryKey: ["me"],
    // Gate on auth: the bearer token is wired into the api-client by a parent effect in AuthProvider,
    // so firing this before auth is ready sends a token-less request that resolves with an empty venue
    // list — which makes VenueShellLayout bounce a valid /v/:venueId deep link to /select-venue.
    enabled: isAuthenticated,
    queryFn: ({ signal }) => fetchVenues(signal),
    staleTime: 60_000,
  });
  return {
    user,
    claims,
    venues: query.data ?? [],
    // Stay "loading" through the auth handoff. A DISABLED query reports isLoading === false, so relying
    // on query.isLoading here would let VenueShellLayout fall through to its !venue redirect before the
    // authenticated ["me"] fetch has even run. isPending covers the enabled-but-not-yet-resolved window.
    isLoading: !isAuthenticated || authIsLoading || query.isPending,
    isError: query.isError,
  };
}
